import api from "../api";
import { profileTag, readinessTag } from "./integrationsBeta";
import { fetchAllPages } from "./paginatedList";

export interface IntegrationConfigProfile {
  id: number;
  client: number;
  client_name: string;
  name: string;
  config_schema_key: string;
  config: Record<string, unknown>;
  used_by: number;
  created_at: string;
  updated_at: string;
}

export const configProfilesApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getConfigProfiles: builder.query<
      IntegrationConfigProfile[],
      { client: number; config_schema_key?: string }
    >({
      queryFn: (params, _api, _extra, fetchWithBQ) =>
        fetchAllPages<IntegrationConfigProfile>(
          fetchWithBQ,
          "/integration-platform/config-profiles/",
          params,
        ),
      providesTags: (result) => [
        ...(result ?? []).map((p) => profileTag(p.id)),
        profileTag("LIST"),
      ],
    }),

    getConfigProfile: builder.query<IntegrationConfigProfile, number>({
      query: (id) => ({
        url: `/integration-platform/config-profiles/${id}/`,
        method: "GET",
      }),
      providesTags: (_result, _err, id) => [profileTag(id)],
    }),

    createConfigProfile: builder.mutation<
      IntegrationConfigProfile,
      {
        client: number;
        name: string;
        config_schema_key?: string;
        config: Record<string, unknown>;
      }
    >({
      query: (body) => ({
        url: "/integration-platform/config-profiles/",
        method: "POST",
        body,
      }),
      invalidatesTags: [profileTag("LIST")],
    }),

    updateConfigProfile: builder.mutation<
      IntegrationConfigProfile,
      {
        id: number;
        name?: string;
        config?: Record<string, unknown>;
      }
    >({
      query: ({ id, ...body }) => ({
        url: `/integration-platform/config-profiles/${id}/`,
        method: "PATCH",
        body,
      }),
      // profileTag(id) also refetches every cached integration detail linked
      // to this profile (they provide the tag); readiness "ALL" refreshes the
      // banners of all cached integrations.
      invalidatesTags: (_result, _err, { id }) => [
        profileTag(id),
        profileTag("LIST"),
        readinessTag("ALL"),
      ],
    }),

    deleteConfigProfile: builder.mutation<unknown, number>({
      query: (id) => ({
        url: `/integration-platform/config-profiles/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _err, id) => [
        profileTag(id),
        profileTag("LIST"),
        readinessTag("ALL"),
      ],
    }),

    shareIntegrationConfig: builder.mutation<
      IntegrationConfigProfile,
      { integrationId: number; name: string }
    >({
      query: ({ integrationId, name }) => ({
        url: `/integration-platform/integrations/${integrationId}/share-config/`,
        method: "POST",
        body: { name },
      }),
      invalidatesTags: (_result, _err, { integrationId }) => [
        profileTag("LIST"),
        readinessTag(integrationId),
        { type: "IntegrationMonitor" as const, id: integrationId },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetConfigProfilesQuery,
  useGetConfigProfileQuery,
  useCreateConfigProfileMutation,
  useUpdateConfigProfileMutation,
  useDeleteConfigProfileMutation,
  useShareIntegrationConfigMutation,
} = configProfilesApi;
